import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { SearchSelect } from "@/components/pawkt/SearchSelect";

const NONE = "__none__";

export function FormModal({ open, onOpenChange, title, description, fields, initial = {}, onSubmit, submitLabel = "Save", testid }) {
  const [v, setV] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) { setV(initial); setTouched({}); } }, [open]);

  const set = (k, val) => { setV((s) => ({ ...s, [k]: val })); setTouched((t) => ({ ...t, [k]: true })); };
  // optional per-field validate(value, values) -> error string; blocks submit while any error remains
  const errs = Object.fromEntries(fields.filter((f) => f.validate).map((f) => [f.name, f.validate(v[f.name], v)]).filter(([, e]) => e));
  const hasErr = Object.keys(errs).length > 0;
  const submit = async () => {
    if (hasErr) { setTouched(Object.fromEntries(Object.keys(errs).map((k) => [k, true]))); return; }
    setBusy(true);
    // Errors are surfaced by the caller's mutation onError (toast); swallow here
    // so a rejected mutateAsync doesn't become an unhandled promise rejection.
    try { await onSubmit(v); } catch (_e) { /* handled by caller */ } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg max-h-[88vh] overflow-y-auto" data-testid={testid}>
        <DialogHeader><DialogTitle className="font-display text-xl font-extrabold">{title}</DialogTitle></DialogHeader>
        {description && <p className="text-xs text-slate-500 -mt-2">{description}</p>}
        <div className="grid sm:grid-cols-2 gap-3 py-1">
          {fields.map((f) => (
            <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
              {f.label ? <Label className="text-[11px] font-bold uppercase tracking-wide text-pawkt-text-secondary mb-1.5 block">{f.label}</Label> : null}
              {f.type === "textarea" ? (
                <Textarea data-testid={`field-${f.name}`} value={v[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} placeholder={f.placeholder} className="bg-white" />
              ) : f.type === "custom" ? (
                f.render(v, set)
              ) : f.type === "search" ? (
                <SearchSelect testid={`field-${f.name}`} value={v[f.name]} options={f.options} allowCustom={f.allowCustom} placeholder={f.placeholder} onChange={(val) => set(f.name, val)} />
              ) : f.type === "select" ? (
                <Select value={v[f.name]?.toString() || ""} onValueChange={(val) => set(f.name, val === NONE ? "" : val)}>
                  <SelectTrigger data-testid={`field-${f.name}`} className="bg-white"><SelectValue placeholder={f.placeholder || "Select"} /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    {f.options.map((o) => <SelectItem key={o.value || NONE} value={o.value || NONE}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input data-testid={`field-${f.name}`} type={f.type || "text"} value={v[f.name] ?? ""}
                  onChange={(e) => set(f.name, f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
                  placeholder={f.placeholder} max={f.max} inputMode={f.inputMode} aria-invalid={!!(touched[f.name] && errs[f.name])}
                  className={`bg-white ${touched[f.name] && errs[f.name] ? "border-rose-400" : ""}`} />
              )}
              {touched[f.name] && errs[f.name] ? <p className="mt-1 text-xs text-rose-600" data-testid={`field-${f.name}-error`}>{errs[f.name]}</p> : null}
              {f.hint ? <p className="mt-1 text-[11px] leading-snug text-slate-500">{f.hint}</p> : null}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || hasErr} data-testid="form-submit">
            {busy ? "Saving…" : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
